import { useCallback, useEffect, useMemo } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CollaborativeInboundUpdateState, PageName } from "analytics/events";
import { HEIGHT as HEADER_HEIGHT } from "app/components/Header";
import { useAppBackgroundEffectsStore } from "app/stores/useAppBackgroundEffectsStore";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { useNewOrderBannerStore } from "app/stores/useNewOrderBannerStore";
import { routes } from "config/routes";
import { useInboundEventTracker } from "flows/Inbound/hooks/useInboundEventTracker/useInboundEventTracker";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { useWaitingOrdersSound } from "shared/hooks/useWaitingOrdersSound";
import { Banner as NewOrderBannerUi, BANNER_HEIGHT } from "ui/Banner/Banner";
import { SlideFade } from "ui/Transitions/Transitions";
import { preventPropagation } from "utils/domEvents";
import { isNotNullNorUndefined } from "utils/tsHelpers";

export function NewOrderBanner() {
  const { isAuthenticated } = useAuth0();
  const intl = useIntl();
  const navigate = useNavigate();
  const { pageName } = useLayoutStore();
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const { droppingListId, deliverySSCC, creatorId } = useInboundStore();

  const waitingOrdersBannerType = useLayoutStore((state) => state.withWaitingOrdersBannerOfType);
  const { summaryOfNextOrderForPicking } = useAppBackgroundEffectsStore(
    useShallow((state) => ({
      summaryOfNextOrderForPicking: state.summaryOfNextOrderForPicking,
    })),
  );

  const { canShowBanner, setNewOrderPending, setUserClosedBanner, reset } = useNewOrderBannerStore(
    useShallow((state) => ({
      canShowBanner: state.canShowBanner,
      setNewOrderPending: state.setNewOrderPending,
      setUserClosedBanner: state.setUserClosedBanner,
      reset: state.reset,
    })),
  );

  const setUserClosed = useCallback(() => {
    setUserClosedBanner();
  }, [setUserClosedBanner]);

  const handleClickBanner = useCallback(() => {
    if (pageName === PageName.INBOUND_DROPPING_PAGE) {
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.DroppingListPaused,
        droppingListId,
        sscc: deliverySSCC,
        inboundingType: null,
        creatorId,
      });
    }

    navigate(routes.pickingV2.root);
  }, [creatorId, deliverySSCC, droppingListId, navigate, pageName, sendInboundStateUpdate]);

  useEffect(() => {
    if (isNotNullNorUndefined(summaryOfNextOrderForPicking)) {
      if (waitingOrdersBannerType !== "none") {
        setNewOrderPending(summaryOfNextOrderForPicking.id);
      }
    } else {
      reset();
    }
  }, [reset, setNewOrderPending, summaryOfNextOrderForPicking, waitingOrdersBannerType]);

  const shouldShowWaitingOrdersBanner = useMemo(() => {
    return isAuthenticated && canShowBanner && waitingOrdersBannerType !== "none";
  }, [canShowBanner, isAuthenticated, waitingOrdersBannerType]);

  useWaitingOrdersSound(!shouldShowWaitingOrdersBanner);

  const bannerUI = useMemo(() => {
    if (waitingOrdersBannerType === "disabled-unidentified") {
      return (
        <NewOrderBannerUi
          title={intl.formatMessage({ id: "components.toast.new-order-toast-title" })}
          onClose={preventPropagation(setUserClosed)}
          description={intl.formatMessage({
            id: "components.toast.new-order-toast-disabled-text",
          })}
        />
      );
    }
    return (
      <NewOrderBannerUi
        onClickBanner={handleClickBanner}
        title={intl.formatMessage({ id: "components.toast.new-order-toast-title" })}
        onClose={preventPropagation(setUserClosed)}
        buttonText={intl.formatMessage({
          id: "components.toast.new-order-toast-start-picking",
        })}
        dataTestId="new-order-banner"
      />
    );
  }, [handleClickBanner, intl, setUserClosed, waitingOrdersBannerType]);

  if (!shouldShowWaitingOrdersBanner) {
    return null;
  }

  return (
    <Box position="absolute" top={HEADER_HEIGHT} left="0" right="0" zIndex="banner">
      <SlideFade unmountOnExit in={shouldShowWaitingOrdersBanner} offsetY={`-${BANNER_HEIGHT}`}>
        {bannerUI}
      </SlideFade>
    </Box>
  );
}
